import { useEffect, useState } from "react";
import { useAuth } from "./hooks/useAuth";
import { useCrypto } from "./hooks/useCrypto";
import { LoginPage } from "./pages/LoginPage";
import { ChatPage } from "./pages/ChatPage";
import { InvitePage } from "./pages/InvitePage";
import { TotpSetupGate } from "./pages/TotpSetupGate";
import { AdminPage } from "./pages/AdminPage";
import { initTheme } from "./lib/theme";
import { useIdleLogout } from "./hooks/useIdleLogout";
import { requestNotificationPermission, initPushNotifications, subscribeToPush } from "./lib/notifications";
import { isBusy, onBusyChange } from "./lib/busy-guard";

/** Simple hash-based route parsing */
function useRoute() {
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  return { path, navigate: (to: string) => { window.history.pushState({}, "", to); setPath(to); } };
}

export function App() {
  const { isAuthenticated, isLoading, restoreSession, user } = useAuth();
  const { path, navigate } = useRoute();
  const [totpSetupRequired, setTotpSetupRequired] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isSecondaryTab, setIsSecondaryTab] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const cryptoHealth = useCrypto((s) => s.cryptoHealth);

  // Auto-logout after 30 minutes of inactivity
  useIdleLogout();

  // Auto-update: banner if visible, silent reload if hidden. Boot-Safety-Net on first check.
  // Never reload while a crypto operation is in flight (isBusy) — would break mid-ratchet.
  // Silent reload runs at most ONCE per browser session: if a stale build keeps reporting
  // a mismatch, we fall back to the banner instead of looping reloads in a hidden tab
  // (which previously caused background restore failures and surprise logouts).
  useEffect(() => {
    if (!isAuthenticated) return;
    const currentVersion = (typeof __BUILD_VERSION__ !== "undefined") ? __BUILD_VERSION__ : null;
    if (!currentVersion) return;
    let isFirstCheck = true;
    let pendingReload = false;

    const SILENT_RELOAD_FLAG = "kp_autoupdate_silent_reload";
    const hasSilentReloaded = () => {
      try { return sessionStorage.getItem(SILENT_RELOAD_FLAG) === "1"; }
      catch { return false; }
    };
    const markSilentReloaded = () => {
      try { sessionStorage.setItem(SILENT_RELOAD_FLAG, "1"); } catch { /* ignore */ }
    };

    const tryReloadIfSafe = () => {
      if (!pendingReload || !document.hidden || isBusy()) return;
      if (hasSilentReloaded()) {
        // Already used our one silent reload — show banner instead.
        setUpdateAvailable(true);
        return;
      }
      console.log("[auto-update] Reloading now (hidden & idle)…");
      markSilentReloaded();
      window.location.reload();
    };

    const checkVersion = async () => {
      try {
        const res = await fetch("/build-version.json", { cache: "no-store" });
        if (!res.ok) return;
        const { v } = await res.json();
        if (!v || v === currentVersion) return;

        // Boot-Safety-Net: running an old bundle right after load. Browser probably
        // served a stale cached copy despite cache-control headers. Hard-reload once
        // with ?v=<new> as cache-buster. If ?v= is already set → loop guard, fall
        // through to the normal banner/silent-reload path. Skipped if busy or if
        // we've already used our silent reload this session.
        if (isFirstCheck && !isBusy() && !hasSilentReloaded()) {
          const url = new URL(window.location.href);
          if (!url.searchParams.has("v")) {
            console.log("[auto-update] Boot mismatch, hard-reloading with ?v=" + v);
            markSilentReloaded();
            url.searchParams.set("v", v);
            window.location.replace(url.toString());
            return;
          }
          console.warn("[auto-update] Still stale after ?v= reload, showing banner");
        }

        pendingReload = true;
        if (document.hidden && !isBusy() && !hasSilentReloaded()) {
          console.log("[auto-update] New build detected (tab hidden & idle), reloading…");
          markSilentReloaded();
          window.location.reload();
        } else {
          setUpdateAvailable(true);
        }
      } catch { /* ignore fetch errors */ }
      finally { isFirstCheck = false; }
    };
    checkVersion();
    const interval = setInterval(checkVersion, 60_000);
    document.addEventListener("visibilitychange", tryReloadIfSafe);
    const unsubBusy = onBusyChange(tryReloadIfSafe);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", tryReloadIfSafe);
      unsubBusy();
    };
  }, [isAuthenticated]);

  // Warn user before unload if a crypto op is in flight — protects against
  // accidental browser refresh / tab close mid-ratchet.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isBusy()) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires a non-empty value
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Multi-tab guard: only one tab holds the E2EE crypto lock. If we don't get
  // it within 500ms, another tab is primary — show a warning banner so the
  // user closes the other tab (avoids dueling writes to crypto IndexedDB).
  useEffect(() => {
    if (typeof navigator === "undefined" || !("locks" in navigator)) return;
    let gotLock = false;
    let cancelled = false;

    navigator.locks.request("kollegeplus-crypto", { mode: "exclusive" }, async () => {
      if (cancelled) return;
      gotLock = true;
      setIsSecondaryTab(false);
      // Hold the lock for the lifetime of this tab.
      return new Promise<void>(() => { /* never resolves */ });
    }).catch(() => { /* ignore */ });

    const timer = setTimeout(() => {
      if (!gotLock && !cancelled) setIsSecondaryTab(true);
    }, 500);

    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  useEffect(() => {
    initTheme();
    restoreSession();
    requestNotificationPermission();

  }, [restoreSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for TOTP setup required events from API client
  useEffect(() => {
    const handler = () => setTotpSetupRequired(true);
    window.addEventListener("totp-setup-required", handler);
    return () => window.removeEventListener("totp-setup-required", handler);
  }, []);

  // F7: Listen for session-expired events (refresh token invalidated by server)
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, []);

  // Initialize push notifications after successful authentication
  useEffect(() => {
    if (isAuthenticated) {
      // Check if user needs TOTP setup
      if (user && !user.totpEnabled) {
        setTotpSetupRequired(true);
      }

      initPushNotifications()
        .then(() => subscribeToPush())
        .catch((err) => console.warn("Push notification setup failed:", err));

      // If user just registered via invite, redirect to main page
      if (path.startsWith("/invite/")) {
        navigate("/");
      }
    }
  }, [isAuthenticated, user]);

  const renderRoute = () => {
    if (isLoading) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            background: "var(--color-bg-primary)",
            color: "var(--color-text-tertiary)",
          }}
        >
          <p>Wird geladen...</p>
        </div>
      );
    }

    // Handle invite route
    const inviteMatch = path.match(/^\/invite\/([a-f0-9]+)$/);
    if (inviteMatch && !isAuthenticated) {
      return <InvitePage token={inviteMatch[1]!} onNavigateToLogin={(token) => navigate(`/?invite=${token}`)} />;
    }

    if (isAuthenticated && totpSetupRequired) {
      return <TotpSetupGate onComplete={() => setTotpSetupRequired(false)} />;
    }

    if (isAuthenticated) {
      // Admin panel — only accessible to owner + admin
      if (path === "/admin") {
        const canAccessAdmin = user?.orgRole === "owner" || user?.orgRole === "admin";
        if (canAccessAdmin) {
          return <AdminPage onBack={() => { window.history.pushState({}, "", "/"); navigate("/"); }} />;
        }
        // Non-admins get redirected to root
        navigate("/");
      }
      return <ChatPage />;
    }

    // Check for invite token in query params (after redirect from invite page)
    const urlParams = new URLSearchParams(window.location.search);
    const inviteToken = urlParams.get("invite") ?? undefined;

    return <LoginPage initialInviteToken={inviteToken} />;
  };

  return (
    <>
      {isSecondaryTab && <SecondaryTabBanner />}
      {updateAvailable && <UpdateBanner onReload={() => window.location.reload()} offset={isSecondaryTab} />}
      {sessionExpired && <SessionExpiredBanner onDismiss={() => setSessionExpired(false)} />}
      {cryptoHealth === 'broken' && <CryptoBrokenBanner />}
      {renderRoute()}
    </>
  );
}

function SessionExpiredBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10001,
        padding: "0.75rem 1rem",
        background: "var(--color-error, #dc2626)",
        color: "#fff",
        fontSize: "0.875rem",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span>Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.</span>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: "0.3rem 0.75rem", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.5)", borderRadius: "0.375rem", color: "#fff", cursor: "pointer", fontWeight: 600 }}
      >
        Neu laden
      </button>
      <button
        onClick={onDismiss}
        aria-label="Schließen"
        style={{ padding: "0.3rem 0.5rem", background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", fontSize: "1rem" }}
      >
        ✕
      </button>
    </div>
  );
}

function SecondaryTabBanner() {
  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        padding: "0.75rem 1rem",
        background: "var(--color-brand-orange, #E67E22)",
        color: "#fff",
        fontSize: "0.875rem",
        fontWeight: 500,
        textAlign: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      }}
    >
      KollegePlus ist bereits in einem anderen Tab geöffnet. Bitte schließe den anderen Tab, um Konflikte zu vermeiden.
    </div>
  );
}

function UpdateBanner({ onReload, offset }: { onReload: () => void; offset: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        top: offset ? "3.5rem" : "1rem",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.5rem 0.5rem 0.5rem 1rem",
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border-primary)",
        borderRadius: "999px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        color: "var(--color-text-primary)",
        fontSize: "0.875rem",
      }}
    >
      <span>Neue Version verfügbar</span>
      <button
        onClick={onReload}
        style={{
          padding: "0.375rem 0.875rem",
          background: "var(--color-primary)",
          color: "#fff",
          border: "none",
          borderRadius: "999px",
          fontSize: "0.875rem",
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Jetzt laden
      </button>
    </div>
  );
}

// F19c-ui: crypto-broken banner
function CryptoBrokenBanner() {
  const onLogout = async () => {
    try {
      const { eraseAllCryptoData } = await import("./lib/crypto-storage");
      await eraseAllCryptoData();
    } catch { /* proceed with cleanup regardless */ }
    try {
      const { clearTokens } = await import("./lib/api");
      clearTokens();
    } catch { /* ignore */ }
    try {
      localStorage.removeItem("kollegeplus:deviceId");
      localStorage.removeItem("kp_keys_fingerprint");
      localStorage.removeItem("kp_idk_fingerprint");
      localStorage.removeItem("kp_bot_session_reset_v2");
    } catch { /* ignore */ }
    window.location.reload();
  };

  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        top: "3rem",
        left: 0,
        right: 0,
        zIndex: 10002,
        padding: "0.75rem 1rem",
        background: "var(--color-error, #dc2626)",
        color: "#fff",
        fontSize: "0.875rem",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
    >
      <span>Lokale Verschlüsselung defekt. Du kannst Nachrichten weder lesen noch senden. Bitte abmelden und Browser-Daten löschen.</span>
      <button
        onClick={onLogout}
        style={{ padding: "0.3rem 0.75rem", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.5)", borderRadius: "0.375rem", color: "#fff", cursor: "pointer", fontWeight: 600 }}
      >
        Jetzt abmelden
      </button>
    </div>
  );
}
